// Persists which request tabs are open, per workspace.
//
// The URL is the source of truth: `?tabs=req_a,req_b,req_c` keeps the open-
// tab list shareable + deep-linkable. localStorage acts as a fallback so a
// fresh visit (no tabs param) still resurrects the prior session. Every
// mutation writes both.

import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

const PARAM = "tabs";

function lsKey(workspaceId: string): string {
  return `rest.openTabs:${workspaceId}`;
}
function readLS(workspaceId: string): string[] {
  try {
    const v = localStorage.getItem(lsKey(workspaceId));
    if (!v) return [];
    const parsed = JSON.parse(v);
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === "string") : [];
  } catch { return []; }
}
function writeLS(workspaceId: string, tabs: string[]): void {
  try { localStorage.setItem(lsKey(workspaceId), JSON.stringify(tabs)); } catch { /* ignore */ }
}

function parseParam(v: string | null): string[] {
  if (!v) return [];
  return v.split(",").map((s) => s.trim()).filter(Boolean);
}

export function useOpenTabs(workspaceId: string, activeId: string | null) {
  const [sp, setSp] = useSearchParams();

  // Tabs come from the URL first; on the very first visit (no param yet),
  // hydrate from localStorage.
  const urlTabs = useMemo(() => parseParam(sp.get(PARAM)), [sp]);
  const tabs = useMemo<string[]>(() => {
    if (urlTabs.length > 0) return urlTabs;
    return readLS(workspaceId);
  }, [urlTabs, workspaceId]);

  // Shared updater: persists to both URL and localStorage, then returns. We
  // accept either a value or an updater so callers can read-modify-write.
  const setTabs = useCallback((next: string[] | ((prev: string[]) => string[])) => {
    const computed = typeof next === "function" ? next(tabs) : next;
    writeLS(workspaceId, computed);
    const params = new URLSearchParams(sp);
    if (computed.length === 0) params.delete(PARAM);
    else params.set(PARAM, computed.join(","));
    setSp(params, { replace: true });
  }, [tabs, workspaceId, sp, setSp]);

  // Auto-add activeId when navigation hits a new request.
  useEffect(() => {
    if (!activeId) return;
    if (tabs.includes(activeId)) return;
    setTabs([...tabs, activeId]);
  }, [activeId, tabs, setTabs]);

  const close = useCallback((id: string) => {
    setTabs((prev) => prev.filter((t) => t !== id));
  }, [setTabs]);

  const closeAll = useCallback(() => setTabs([]), [setTabs]);

  const reorder = useCallback((from: number, to: number) => {
    setTabs((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, [setTabs]);

  return { tabs, close, closeAll, reorder, setTabs };
}
